import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {IPositionListItemModel} from '../../core/interfaces/position-list-item.interface';
import {ISelectItemModel} from '../../core/interfaces/select-item.interface';
import {AuthService} from '../../auth/auth.service';
import {GridOptions, GridApi, GridReadyEvent, ColumnState} from "ag-grid-community";
import {JobDescriptionRendererComponent} from '../job-description-renderer/job-description-renderer.component';
import {EditButtonRendererComponent} from '../edit-button-renderer/edit-button-renderer.component';
import {ViewButtonRendererComponent} from '../view-button-renderer/view-button-renderer.component';
import {FilterPositionsStatusComponent} from '../filter-grid-status/filter-grid-status.component';

@Component({
    selector: 'app-list-positions',
    templateUrl: './list-positions.component.html',
    styleUrls: ['./list-positions.component.css'],
    host: {
        '(window:resize)': 'onResize($event)'
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ListPositionsComponent implements OnInit {

  public gridOptions: GridOptions;
  private gridApi!: GridApi;
  private gridReady = false;
  private dataReady = false;

 onGridReady = (e: GridReadyEvent) => {
    this.gridApi = e.api;
    this.gridReady = true;
    this.tryInitGrid();
  };

   private tryInitGrid() {
    if (!this.gridReady || !this.dataReady) return;

    // safe to use gridApi now
    this.gridApi.setGridOption('rowData', this.createRowData());
    this.loadGridPreferences();
    this.setPageSize(this.selectedPaginationOption);
  }

  public inputQuickSearch: string;
  public selectedPaginationOption = 2;
  public paginationOptions = [
    {id: 0, name: "10"},
    {id: 1, name: "50"},
    {id: 2, name: "100"},
    {id: 3, name: "500"}
  ];

  private createColumnDefs() {
    return [
      {
        headerName: "Job Description",
        field: "description",
        menuTabs: ['filterMenuTab'],
        filter: "agTextColumnFilter",
        width: 230,
        cellRenderer: ViewButtonRendererComponent,
        icons: {
          sortAscending: '<i class="fa fa-sort-alpha-asc"/>',
          sortDescending: '<i class="fa fa-sort-alpha-desc"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      },
      {
        headerName: "Id",
        field: "positionId",
        filter: "agNumberColumnFilter",
        menuTabs: ['filterMenuTab'],
        width: 75,
        icons: {
          sortAscending: '<i class="fa fa-arrow-up"/>',
          sortDescending: '<i class="fa fa-arrow-down"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      },
      {
        headerName: "Department",
        field: "orgUnitName",
        filter: "agTextColumnFilter",
        menuTabs: ['filterMenuTab'],
        icons: {
          sortAscending: '<i class="fa fa-sort-alpha-asc"/>',
          sortDescending: '<i class="fa fa-sort-alpha-desc"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      },
      {
        headerName: "Level",
        field: "positionLevelName",
        filter: "agTextColumnFilter",
        menuTabs: ['filterMenuTab'],
        icons: {
          sortAscending: '<i class="fa fa-sort-alpha-asc"/>',
          sortDescending: '<i class="fa fa-sort-alpha-desc"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      },
      {
        headerName: "Function",
        field: "name",
        filter: "agTextColumnFilter",
        menuTabs: ['filterMenuTab'],
        icons: {
          sortAscending: '<i class="fa fa-sort-alpha-asc"/>',
          sortDescending: '<i class="fa fa-sort-alpha-desc"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      },
      {
        headerName: "Created By",
        field: "createdBy",
        filter: "agTextColumnFilter",
        menuTabs: ['filterMenuTab'],
        icons: {
          sortAscending: '<i class="fa fa-sort-alpha-asc"/>',
          sortDescending: '<i class="fa fa-sort-alpha-desc"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      },
      {
        headerName: "Created Date",
        field: "createdDate",
        filter: "agDateColumnFilter",
        menuTabs: ['filterMenuTab'],
        valueFormatter: this.dateCellFormatter,
        sort: 'desc',
        width: 120,
        icons: {
          sortAscending: '<i class="fa fa-arrow-up"/>',
          sortDescending: '<i class="fa fa-arrow-down"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      },
      {
        headerName: "Job Description",
        field: "description",
        menuTabs: ['filterMenuTab'],
        filter: "agTextColumnFilter",
        suppressSizeToFit: true,
        width: 130,
        cellRenderer: JobDescriptionRendererComponent,
        icons: {
          sortAscending: '<i class="fa fa-sort-alpha-asc"/>',
          sortDescending: '<i class="fa fa-sort-alpha-desc"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      },
      {
        headerName: "Action",
        field: "value",
        suppressSizeToFit: true,
        menuTabs: [],
        width: 95,
        suppressFilter: true,
        cellRenderer: EditButtonRendererComponent,
      },
      {
        headerName: "Active",
        field: "isActiveDescription",
        filter: FilterPositionsStatusComponent,
        menuTabs: ['filterMenuTab'],
        width: 110,
        icons: {
          sortAscending: '<i class="fa fa-sort-alpha-asc"/>',
          sortDescending: '<i class="fa fa-sort-alpha-desc"/>',
          filter: '<i class="fa fa-search"/>',
          menu: '<i class="fa fa-filter"/>'
        }
      }
    ];
  }

  private createRowData() {
    return this.selectedClientPositions;
  }

 public exportDataAsCsv(){
    if (this.gridApi) this.gridApi.exportDataAsCsv();
  }

  // Move this to shared?
  private dateCellFormatter(params) {
    const thisDate: Date = new Date(params.value);
    return `${(thisDate.getMonth() + 1)}/${thisDate.getDate()}/${thisDate.getFullYear()}`;
  }

  public clients: ISelectItemModel[];
  public positions: IPositionListItemModel[];
  public selectedClient: ISelectItemModel;

  constructor(private route: ActivatedRoute, private router: Router, private authService: AuthService)
  {
    this.gridOptions = <GridOptions>{
      onGridReady: this.onGridReady,
      columnDefs: this.createColumnDefs(),
      rowHeight: 48, // recommended row height for material design data grids,
      headerHeight: 38,
      suppressCellFocus: true,
      suppressMenuHide: true,
      animateRows: true,
      pagination: true,
      getRowClass: (event) => this.rowBackgroundColor(event),
      onSortChanged: () => this.onColumnChanged(),
      onColumnResized: () => this.onColumnChanged(),
      onFilterChanged: (event) => this.onFilterChanged(event),
      context: { componentParent: this },   // Make this component (and its members) available to child components.
      defaultColDef: {
        resizable: true,
        sortable: true
      }
    }
  }

  onResize(event) {
    localStorage.setItem('positionsWindowWidth', JSON.stringify(event.target.innerWidth));
    this.gridApi.sizeColumnsToFit();
  }

  public rowBackgroundColor(event): string {
    if (event.node.rowIndex % 2 === 0) {
      return 'shaded-row';
    }
    else
      return null;
  }

  public setPageSize(paginationOption) {
    // Current version of AG Grid appears to have problems with autopagination (at least if it's turned on at runtime)... disabling.
    // if(paginationOption != 0)
    // {
      this.gridOptions.paginationAutoPageSize = false;
      this.gridApi.setGridOption('paginationPageSize',Number( (this.paginationOptions[paginationOption]) ? this.paginationOptions[paginationOption].name : 100));
    // }
    // else
    // {
    //   this.gridOptions.paginationAutoPageSize = true;
    //   //this.gridOptions.api.doLayout();
    //   this.gridOptions.api.redrawRows();
    // }
  }

  public ngOnInit() {
    const data = this.route.snapshot.data as any; 
    this.clients = data?.clients ?? [];
    this.positions = data?.positions ?? [];

    if (this.clients.length) {
      if (this.isAdmin) {
        const key = 'gridPositionsSelectedClient' + this.getImpersonatedUserId();
        const selectedClientId = localStorage.getItem(key);
        this.selectedClient = selectedClientId != null
          ? this.getClientFromId(this.clients, Number(selectedClientId))
          : null;
      } else {
        this.selectedClient = this.authService.impersonatedUser != null
          ? this.getClientFromId(this.clients, this.authService.impersonatedUser.clientId)
          : this.getClientFromId(this.clients, this.authService.clientId);
      }
    }

    this.dataReady = true;
    this.tryInitGrid();
  }

  public ngAfterViewInit() {
  }

  public loadGridPreferences() {

     if (!this.gridApi) return;
    //Handle loading saved Sort, Filter, and Column settings.
    var columnState = localStorage.getItem('gridPositionsColumnState' + this.getImpersonatedUserId());  //gridColumnState'+this.getImpersonatedUserId(), JSON.stringify(this.gridOptions.columnApi.getColumnState()
    if (columnState != null) {
      this.gridApi.applyColumnState({ state: JSON.parse(columnState), applyOrder: true });      //Prevent poor column saved states when a user's window size changes between sessions.
      if (+localStorage.getItem('positionsWindowWidth') != window.innerWidth) {
        this.gridApi.sizeColumnsToFit();
        localStorage.setItem('positionsWindowWidth', JSON.stringify(window.innerWidth));
      }
    }
    else {
      this.gridApi.sizeColumnsToFit();
      this.resetFilters();
    }
    var filterModel = localStorage.getItem('gridPositionsFilterModel' + this.getImpersonatedUserId());
    if (filterModel != null) {
      const thisModel = JSON.parse(filterModel);
      this.gridApi.setFilterModel(thisModel);    
    }
    else
      this.resetFilters();
    //Also attempt to load pagination selection
    var paginationOption = localStorage.getItem('gridPositionsPagination' + this.getImpersonatedUserId());
    if (paginationOption != null)
      this.onChangePaginationOption(Number(paginationOption));
  }

  public getClientFromId(clients: ISelectItemModel[], id: number): ISelectItemModel{
    var output: ISelectItemModel;
    for (let client of clients){
      if (client.id === id) {
        output = client;
      }
    }
    return output;
  }

  public onFilterTextBoxChanged() {
    this.gridApi.setGridOption('quickFilterText', this.inputQuickSearch);
  }

  public resetFilters() {
    if (this.isAdmin) {
      this.selectedClient = null;
      this.onSelectClient(null);
    }
    this.onChangePaginationOption(2);

    if (!this.gridApi) return; 

    this.gridApi.setFilterModel(null);
    this.inputQuickSearch = '';
    this.gridApi.setGridOption('quickFilterText', '');
    this.gridApi.resetColumnState();
    this.gridApi.onSortChanged();
    this.gridApi.onFilterChanged();
    this.gridApi.sizeColumnsToFit();
  }

  public onChangePaginationOption(option) {
    this.selectedPaginationOption = option; // event.target.value;
    this.setPageSize(this.selectedPaginationOption);
    localStorage.setItem('gridPositionsPagination' + this.getImpersonatedUserId(), this.selectedPaginationOption.toString());
  }

  public onColumnChanged() {
    localStorage.setItem('gridPositionsColumnState' + this.getImpersonatedUserId(), JSON.stringify(this.gridApi.getColumnState())); //get/applyColumnState()
  }

  public onFilterChanged(event) {
    localStorage.setItem('gridPositionsFilterModel' + this.getImpersonatedUserId(), JSON.stringify(this.gridApi.getFilterModel()));
  }

  // This event occurs when an admin selects a client -> The grid then needs to be populated.
 public onSelectClient(event): void {
    this.selectedClient = event;

    if (event != null) {
      localStorage.setItem('gridPositionsSelectedClient' + this.getImpersonatedUserId(), event.id.toString());
    } else {
      localStorage.removeItem('gridPositionsSelectedClient' + this.getImpersonatedUserId());
    }

    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.createRowData());
      this.gridApi.sizeColumnsToFit();
    }
  }

  public get isRoleClient() {
    return this.authService.isRoleClient;
  }

  public getImpersonatedUserId(): string {
    return (this.authService.impersonatedUser != null) ? this.authService.impersonatedUser.userId.toString() : this.authService.userId.toString();
  }

  public onNewClick() {
    this.router.navigate(['/positions', 'add', 'client', this.selectedClient.id]);
  }

  public onClickEdit(positionId: number) {
    this.router.navigate(['/positions', 'edit', positionId], {queryParams: {clientId: this.selectedClient.id}});
  }

  public onJobDescriptionClick(attachmentId: number) {
    alert('JobDescriptionAttachmentId: ' + attachmentId);
  }

  public get selectedClientPositions(): IPositionListItemModel[] {
    if (!this.selectedClient) {
      return [];
    }

    const positions = this.positions.filter((p) => p.clientId === this.selectedClient.id);

    if (this.authService.orgUnitIds === null ) {
      return positions;
    } else {
      // JSG: Added check for position orgunit is null to include positions not assigned to a department in filter.
      return positions.filter((p) => this.authService.orgUnitIds.includes(p.orgUnitId) || p.orgUnitId === null);
    }
  }

  public get isAdmin(): boolean {
    return this.authService.isRoleAdmin;
  }
}
